import { DmmEnvConsumer, DmmEnvProvider, DmmEnvType } from 'app/components/auth/DmmEnvQueryContext';
import GetOauthResult from 'app/components/auth/GetOauthResult';
import { AuthProvidersParamConsumer, AuthProvidersParamProvider } from 'app/components/auth/ProviderQueryContext';
import { QueryConsumer } from 'app/components/Contexts';
import { tFunctionType, useI18n } from 'app/components/localization';
import { useHistory } from 'app/components/router/History';
import appConfig from 'app/config/client';
import DeployInfo, { RunningIn } from 'app/config/types/DeployInfo';
import { QueryParameter } from 'app/constants/QueryParameter';
import {
  authenticate,
  forgotPassword,
  startAttachExternalOauth,
  startAuthenticateWithOauth,
} from 'app/models/auth/actions';
import { isAuthenticatedSelector } from 'app/models/auth/selectors';
import { notify } from 'app/services/errorReporter';
import { History } from 'app/services/history';
import { LoginIntent } from 'app/services/oauth/constants';
import { DMMAuthService, DMMLoginState } from 'app/services/oauth/DMMAuthService';
import OauthError, { isEmailTakenError, isFacebookError } from 'app/services/oauth/OauthError';
import { AppState } from 'app/store';
import createErrorType from 'app/utils/createErrorType';
import { getWithDefault } from 'app/utils/fpTs';
import { parseBoolean } from 'app/utils/parse';
import { Set } from 'immutable';
import React, { useContext } from 'react';
import { connect } from 'react-redux';
import { AppDispatch } from 'redux';
import { FACEBOOK, AuthProvider, DMM, AUTH_PROVIDERS, PASSWORD, DMM_STAGING, DMM_TEST } from 'app/constants/enums/AuthProvider';
import { BrandContextValue, useBrand } from '../OrganizationContext';
import { decodeWithDefault } from 'app/utils/ioTs';

const { deployInfo } = appConfig;

export class LoginFailure extends createErrorType('LoginFailure') {
  public authProvider: AuthProvider;
  public cause: any;

  public constructor(t: tFunctionType, cause: any) {
    super(t('auth.error.unexpected'));
    this.cause = cause;
    this.authProvider = PASSWORD;
  }
}

class ExternalOauthLoginFailure extends LoginFailure {
  public constructor(t: tFunctionType, provider: AuthProvider, cause: any) {
    super(t, cause);
    this.authProvider = provider;
  }
}

class ExternalOauthCallbackFailure extends LoginFailure {
  public constructor(t: tFunctionType, cause: unknown) {
    super(t, cause);
    // User rejection should be expressed as code=AccessDenied, extended_code=ExternalRejection.
    // Unexpected rejections will be code=AccessDenied.
    // A bug in the omniauth-facebook gem returns code=ServerError for _all_ failures.

    this.authProvider = cause instanceof OauthError ? (this.authProvider ?? PASSWORD) : PASSWORD;

    this.message = isFacebookError(cause)
      ? t('page.oauth-callback.facebook-error-message')
      : t('page.oauth-callback.generic-error-message');
  }
}

class CredentialsLoginFailure extends LoginFailure {
  public constructor(t: tFunctionType, cause: any) {
    super(t, cause);
    if (cause instanceof OauthError) {
      const msg = this.cause.localizedErrorMessage(t);
      this.message = msg || t('auth.error.unexpected');
    }
  }
}

class ValidationCredentialsMissing extends LoginFailure {
  public constructor(t: tFunctionType, hasUsername: boolean, hasPassword: boolean) {
    if (hasUsername && hasPassword) {
      throw new Error('Both credentials were present');
    }
    super(t, undefined);
    this.message = (!hasUsername && !hasPassword) ? t('auth.error.empty-username-and-password')
      : !hasUsername ? t('auth.error.empty-username')
      : t('auth.error.empty-password');
  }
}

export enum LoginLoadingState {
  NOT_LOADING,
  // The initializing state is used while hitting the API to check the DMM cookie in order to decide
  // which buttons to show to the user.
  LOADING_INITIALIZING,
  LOADING_AUTO_LOGIN,
  LOADING_CREDENTIALS,
  LOADING_DMM,
  LOADING_DMM_REGISTRATION,
  LOADING_FACEBOOK,
}

export interface ILoginContext {
  loadingState: LoginLoadingState;
  error?: any;
  errorAuthProvider?: AuthProvider;
  loginWithCredentials: LoginProvider['loginWithCredentials'];
  loginWithDMM: LoginProvider['loginWithDMM'];
  registerWithDMM: LoginProvider['registerWithDMM'];
  loginWithFacebook: LoginProvider['loginWithFacebook'];
  dmmLoginEnv?: DmmEnvType;
  forceAutoLogin?: boolean;
  intent: LoginIntent;
  allowPasswordAuth: boolean;
  allowedExternalAuths: Set<AuthProvider>;
  allowSignup: boolean;
  emailTaken: boolean;
  dmmLoginState: DMMLoginState | undefined;
  showAllAuthMethods?: boolean;
}

const LoginContext = React.createContext<ILoginContext | undefined>(undefined);

interface IProps {
  onSuccess?: () => void;
  forceAutoLogin?: boolean;
  intent: LoginIntent;
}

interface IPropsWithDeps extends IProps {
  t: tFunctionType;
  dispatch: AppDispatch;
  oauthError: unknown;
  brand: BrandContextValue;
  history: History;
  dmmLoginEnv?: DmmEnvType;
  forceAutoLogin?: boolean;
  authenticated: boolean;
  showAllAuthMethods: boolean;
  specifiedProviders?: Set<AuthProvider>;
  allowSignup: boolean;
}

interface IState {
  error: LoginFailure | undefined;
  prevOauthError: unknown;
  loadingState: LoginLoadingState;
  emailTaken: boolean;
  dmmLoginState: DMMLoginState | undefined;
}

class LoginProvider extends React.Component<IPropsWithDeps, IState> {
  public static getDerivedStateFromProps(props: IPropsWithDeps, state: IState) {
    const { t, oauthError } = props;

    const newState: Partial<IState> = {};

    let { error } = state;
    const { dmmLoginState } = state;

    if (oauthError && oauthError !== state.prevOauthError) {
      error = new ExternalOauthCallbackFailure(t, oauthError);
      Object.assign(newState, { error, prevOauthError: oauthError });
    }

    const { authenticated, forceAutoLogin } = props;
    const { allowPasswordAuth, allowedExternalAuths } =
            LoginProvider.derivedAuthProps(props);
    const wasAfterEmailTaken = error && isEmailTakenError(error.cause) || false;

    const showingOnlyDMM = !allowPasswordAuth && allowedExternalAuths.every(
      (provider) => [DMM.id, DMM_STAGING.id, DMM_TEST.id].includes(provider.id),
    );

    if (
      !authenticated &&
      !error &&
      showingOnlyDMM &&
      (
        forceAutoLogin ||
        // Auto-login if user is logged in to DMM and has a Replace account.
        dmmLoginState === DMMLoginState.HAS_REGISTERED_DMM_LOGIN
      )
    ) {
      newState.loadingState = LoginLoadingState.LOADING_AUTO_LOGIN;
    }

    newState.emailTaken = state.emailTaken || wasAfterEmailTaken;

    return newState;
  }

  private static derivedAuthProps({ showAllAuthMethods, brand, specifiedProviders }: IPropsWithDeps) {
    const authProviders = brand.siteFeatures.users.configuration?.authProviders.toSet() ?? Set<AuthProvider>();
    const allowPasswordAuth = showAllAuthMethods ||
      specifiedProviders !== undefined || authProviders.includes(PASSWORD);
    const allowedExternalAuths = showAllAuthMethods ?
      AUTH_PROVIDERS.toSet() :
      specifiedProviders === undefined ?
      authProviders : specifiedProviders;
    return { allowPasswordAuth, allowedExternalAuths };
  }

  private autoLoginFired = false;
  private dmmAuthService: DMMAuthService;

  public constructor(initialProps: IPropsWithDeps) {
    super(initialProps);

    this.dmmAuthService = new DMMAuthService(initialProps.history);

    this.state = {
      error: undefined,
      prevOauthError: undefined,
      emailTaken: false,
      // We don't need to check the dmm_check endpoint if the user doesn't have a DMM cookie at all.
      loadingState: (
        !this.dmmAuthService.isDMMLoginStateKnown() ?
          LoginLoadingState.LOADING_INITIALIZING
        /* else */ :
          LoginLoadingState.NOT_LOADING
      ),
      dmmLoginState: undefined,
    };
  }

  public async componentDidMount() {
    if (this.state.loadingState === LoginLoadingState.LOADING_INITIALIZING) {
      this.getDMMLoginState();

      // Auto-login will be triggered after the DMM login state check is done.
    } else {
      this.checkAutoLogin();
    }
  }

  public componentDidUpdate(prevProps: IPropsWithDeps, prevState: IState) {
    this.checkAutoLogin();
  }

  public render() {
    const {
      dmmLoginEnv,
      forceAutoLogin,
      intent,
      children,
      allowSignup,
      showAllAuthMethods,
    } = this.props;
    const {
      loadingState,
      error,
      emailTaken,
      dmmLoginState,
    } = this.state;
    const {
      loginWithCredentials,
      loginWithDMM,
      registerWithDMM,
      loginWithFacebook,
    } = this;

    const errorLoginType = error && error.authProvider;

    const { allowPasswordAuth, allowedExternalAuths } = LoginProvider.derivedAuthProps(this.props);

    return (
      <LoginContext.Provider
        value={{
          loadingState,
          error,
          errorAuthProvider: errorLoginType,
          loginWithCredentials,
          loginWithDMM,
          registerWithDMM,
          loginWithFacebook,
          dmmLoginEnv,
          forceAutoLogin,
          intent,
          allowPasswordAuth,
          allowedExternalAuths,
          allowSignup,
          emailTaken,
          dmmLoginState,
          showAllAuthMethods,
        }}
      >
        {children}
      </LoginContext.Provider>
    );
  }

  private async getDMMLoginState() {
    try {
      const dmmLoginState = await this.dmmAuthService.fetchDMMLoginState();

      this.setState({
        loadingState: LoginLoadingState.NOT_LOADING,
        dmmLoginState,
      });
    } catch (error) {
      this.setState({
        loadingState: LoginLoadingState.NOT_LOADING,
      });
    }
  }

  private checkAutoLogin() {
    if (this.state.loadingState === LoginLoadingState.LOADING_AUTO_LOGIN && !this.autoLoginFired) {
      this.autoLoginFired = true;
      this.loginWithDMM();
    }
  }

  private async loginWithExternalOauth(provider: AuthProvider, extraParams = {}, forceIntent?: LoginIntent) {
    const { t, history, intent, brand } = this.props;
    const { pathname, search, hash } = history.location;
    const returnUriReference = `${pathname}${search}${hash}`;

    return await this.withLoginLifecycle(async () => {
      try {
        return await startAuthenticateWithOauth(
          { provider, intent: forceIntent || intent, extraParams, returnUriReference, brandId: brand.id },
          { dmmAuthService: this.dmmAuthService },
        );
      } catch (e) {
        throw new ExternalOauthLoginFailure(t, provider, e);
      }
    });
  }

  private registerWithDMM = () => this.authWithDMM(LoginIntent.Register);

  private loginWithDMM = () => this.authWithDMM();

  private authWithDMM = async (intent?: LoginIntent) => {
    const { dmmLoginEnv } = this.props;

    const extraOptions = dmmLoginEnv ? { dmm_env: dmmLoginEnv } : {};

    const loadingState = (
      intent === LoginIntent.Register ? LoginLoadingState.LOADING_DMM_REGISTRATION
      /* else */                      : LoginLoadingState.LOADING_DMM
    );

    this.setState({ error: undefined, loadingState });
    return await this.loginWithExternalOauth(DMM, extraOptions, intent);
  };

  private loginWithFacebook = async () => {
    this.setState({ error: undefined, loadingState: LoginLoadingState.LOADING_FACEBOOK });
    return await this.loginWithExternalOauth(FACEBOOK);
  };

  private loginWithCredentials = async (username: string, password: string) => {
    const { t, history, dispatch } = this.props;

    this.setState({
      error: undefined,
      loadingState: LoginLoadingState.LOADING_CREDENTIALS,
    });

    return await this.withLoginLifecycle(async () => {
      if (!username || !password) {
        throw new ValidationCredentialsMissing(t, !!username, !!password);
      }

      try {
        await dispatch(authenticate(username, password));
        if (this.state.emailTaken) {
          const { pathname, search, hash } = history.location;
          const returnUriReference = `${pathname}${search}${hash}`;
          const nextURI = await dispatch(startAttachExternalOauth(FACEBOOK, returnUriReference));
          history.hardPush(nextURI);
        }
      } catch (e) {
        if (
          e instanceof OauthError &&
          e.extendedCode === OauthError.ExtendedCodes.PASSWORD_INSUFFICIENT
        ) {
          try {
            // Username should be an email, so automatically send a password reset email to the
            // entered username when the password is insufficient.
            await this.props.dispatch(forgotPassword(username));
          } catch (sendError) {
            throw new CredentialsLoginFailure(t, sendError);
          }
        }

        throw new CredentialsLoginFailure(t, e);
      }
    });
  };

  private async withLoginLifecycle(doLogin: () => Promise<void>) {
    try {
      await doLogin();
    } catch (error) {
      this.setState({ loadingState: LoginLoadingState.NOT_LOADING, error });
      notify(error, { name: 'withLoginLifecycle' });
    }

    if (this.props.onSuccess) {
      this.props.onSuccess();
    }
  }
}

const mapStateToProps = (state: AppState) => ({
  authenticated: isAuthenticatedSelector(state),
});

const ConnectedLoginProvider = connect(mapStateToProps)(LoginProvider);

const LoginProviderInjectDependencies: React.FC<IProps> = (props) => {
  const { t } = useI18n();
  const history = useHistory();
  const brand = useBrand();
  return (
    <DmmEnvProvider>
      <DmmEnvConsumer>{([dmmEnv]) =>
        <AuthProvidersParamProvider>
          <AuthProvidersParamConsumer>{([providers]) =>
            <QueryConsumer>{query => {
              // Let developers and testers simulate production login options
              const runningInEnv =
                deployInfo.runningIn === RunningIn.Production
                ? RunningIn.Production
                : decodeWithDefault(
                    DeployInfo.type.props.runningIn,
                    query.auth_running_in,
                    deployInfo.runningIn,
                );

              const authProviders = brand.siteFeatures.users.configuration?.authProviders.toSet() ?? Set();
              const allowSignup = getWithDefault(
                parseBoolean(query.show_signup),
                authProviders.has(PASSWORD),
              );

              const showAllAuthMethods =
                getWithDefault(parseBoolean(query[QueryParameter.ShowAuth]), runningInEnv === RunningIn.Local);

              return (
                <GetOauthResult>{(result) => (
                  <ConnectedLoginProvider
                    {...props}
                    specifiedProviders={providers}
                    t={t}
                    history={history}
                    oauthError={result && result.error}
                    brand={brand}
                    dmmLoginEnv={dmmEnv}
                    showAllAuthMethods={showAllAuthMethods}
                    allowSignup={allowSignup}
                  />
                )}</GetOauthResult>
              );
            }}</QueryConsumer>
          }</AuthProvidersParamConsumer>
        </AuthProvidersParamProvider>
      }</DmmEnvConsumer>
    </DmmEnvProvider>
  );
};

export { LoginProviderInjectDependencies as LoginProvider };

export const useLoginContext = () => {
  const context = useContext(LoginContext);
  if (context === undefined) {
    throw new Error('useLoginContext requires a <LoginProvider>');
  }
  return context;
};

export const LoginConsumer: React.SFC<{
  children: (loginContext: ILoginContext) => React.ReactNode,
}> = ({ children }) => (
  <LoginContext.Consumer>{loginContext => {
    if (!loginContext) {
      throw new Error('<LoginConsumer> requires a <LoginProvider>');
    }

    return children(loginContext);
  }}</LoginContext.Consumer>
);
